/**
 * RecentCaptures — preview of latest notes.
 *
 * Props:
 *  - notes: array
 *  - loading: boolean
 */

import { useTranslation } from 'react-i18next';

const CAPTURE_ICONS = { text: '✍️', voice: '🎤', photo: '📷', mixed: '📝', chat: '💬' };
const STATUS_ICONS = { completed: '✓', failed: '⚠', pending: '⏳', processing: '⏳' };

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default function RecentCaptures({ notes, loading, onNoteSelect }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-lg p-3 animate-pulse h-16" />
        ))}
      </div>
    );
  }

  if (!notes || notes.length === 0) {
    return (
      <div className="text-[var(--aif-text-muted)] text-sm text-center py-6" data-testid="empty-captures">
        {t('brainDump.emptyState', 'Noch keine Notizen. Tippe auf 🎤 oder 📷 oben.')}
      </div>
    );
  }

  return (
    <ul className="space-y-2" data-testid="recent-captures-list">
      {notes.map((note) => (
        <li
          key={note.id}
          onClick={() => onNoteSelect && onNoteSelect(note.id)}
          role={onNoteSelect ? 'button' : undefined}
          tabIndex={onNoteSelect ? 0 : undefined}
          onKeyDown={onNoteSelect ? (e) => e.key === 'Enter' && onNoteSelect(note.id) : undefined}
          className={`bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-lg p-3 transition hover:shadow-sm ${onNoteSelect ? 'cursor-pointer' : ''}`}
        >
          <div className="flex items-center gap-2 text-sm">
            <span>{CAPTURE_ICONS[note.capture_type] || '📝'}</span>
            <span className="text-[var(--aif-text-muted)]">{formatTime(note.captured_at)}</span>
            <span className="ml-auto text-xs">{STATUS_ICONS[note.processing_status] || '⏳'}</span>
          </div>
          <div className="mt-1 text-sm text-[var(--aif-text-primary)] line-clamp-2">
            {note.structured_summary || note.raw_text || (
              <span className="text-[var(--aif-text-muted)] italic">
                {t('brainDump.processing', 'Wird verarbeitet...')}
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
